import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, ArrowRightIcon, TrendingUpIcon, CheckIcon } from 'lucide-react';
interface RecoveryEstimatorProps {
  open: boolean;
  onClose: () => void;
}
const categories = [{
  label: 'Apparel & accessories',
  qualityFactor: 1
}, {
  label: 'Home & lifestyle',
  qualityFactor: 0.95
}, {
  label: 'Electronics',
  qualityFactor: 0.9
}, {
  label: 'Beauty & wellness',
  qualityFactor: 1
}, {
  label: 'Other consumer goods',
  qualityFactor: 0.92
}];
const recoveryMilestones = [{
  months: 2,
  value: 0.1
}, {
  months: 6,
  value: 0.4
}, {
  months: 9,
  value: 0.5
}, {
  months: 12,
  value: 0.6
}];
const traditionalLiquidationPerMsrpDollar = 0.05;
function formatCurrency(n: number) {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  });
}
function getRecoveryPerMsrpDollar(months: number) {
  const upperMilestone = recoveryMilestones.find(milestone => milestone.months >= months);
  const lowerMilestone = [...recoveryMilestones].reverse().find(milestone => milestone.months <= months);
  if (!upperMilestone) return recoveryMilestones[recoveryMilestones.length - 1].value;
  if (!lowerMilestone || lowerMilestone.months === upperMilestone.months) return upperMilestone.value;
  const progress = (months - lowerMilestone.months) / (upperMilestone.months - lowerMilestone.months);
  return lowerMilestone.value + (upperMilestone.value - lowerMilestone.value) * progress;
}
function formatCents(value: number) {
  return `${Math.round(value * 100)}¢`;
}
export function RecoveryEstimator({
  open,
  onClose
}: RecoveryEstimatorProps) {
  const [excess, setExcess] = useState(500000);
  const [returnRate, setReturnRate] = useState(18);
  const [annualRevenue, setAnnualRevenue] = useState(5000000);
  const [sellWindowMonths, setSellWindowMonths] = useState(6);
  const [brandTakeRate, setBrandTakeRate] = useState(40);
  const [category, setCategory] = useState(categories[0]);
  const [submitted, setSubmitted] = useState(false);
  const manyCoTakeRate = 100 - brandTakeRate;
  const result = useMemo(() => {
    const returnsValue = annualRevenue * (returnRate / 100);
    const totalStranded = excess + returnsValue;
    const baseRecoveryPerMsrpDollar = getRecoveryPerMsrpDollar(sellWindowMonths);
    const recoveryPerMsrpDollar = Math.min(baseRecoveryPerMsrpDollar * category.qualityFactor, 0.6);
    const manyCoRecovery = totalStranded * recoveryPerMsrpDollar;
    const brandProceeds = manyCoRecovery * (brandTakeRate / 100);
    const traditionalLiquidation = totalStranded * traditionalLiquidationPerMsrpDollar;
    return {
      returnsValue,
      totalStranded,
      brandProceeds,
      traditionalLiquidation,
      recoveryPerMsrpDollar,
      low: brandProceeds * 0.85,
      high: brandProceeds * 1.2
    };
  }, [excess, returnRate, annualRevenue, sellWindowMonths, brandTakeRate, category]);
  return <AnimatePresence data-id="element-1426" data-mp="m02dmcbvekao">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto overscroll-contain bg-ink/50 p-0 pb-[env(safe-area-inset-bottom)] backdrop-blur-sm sm:items-center sm:p-6" onClick={onClose} role="dialog" aria-modal="true" aria-label="Inventory recovery estimator" data-id="element-1427" data-mp="26kr174dxqywl">
          <motion.div initial={{
        opacity: 0,
        y: 24,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 24,
        scale: 0.98
      }} transition={{
        type: 'spring',
        damping: 26,
        stiffness: 280
      }} onClick={e => e.stopPropagation()} className="relative min-h-[100dvh] w-full max-w-3xl overflow-hidden rounded-none bg-paper shadow-2xl sm:min-h-0 sm:rounded-3xl md:grid md:grid-cols-[1fr_0.85fr]" data-id="element-1428" data-mp="2kbvuzpumgwz0">
            <button onClick={onClose} className="absolute right-4 top-4 z-10 rounded-full p-1.5 text-forest transition-colors hover:bg-forest/10 hover:text-forest-dark md:text-cream/90 md:hover:bg-cream/10 md:hover:text-cream" aria-label="Close" data-id="element-1429" data-mp="338x5trauxrav">
              <XIcon className="h-5 w-5" data-id="element-1430" data-mp="2itnjofi2apmp" />
            </button>

            {/* Inputs */}
            <div className="p-5 sm:p-6" data-id="element-1431" data-mp="2ivgpxtorj7u6">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-forest" data-id="element-1432" data-mp="36iybv8lcjbp8">
                Inventory Recovery
              </span>
              <h2 className="mt-1 font-display text-xl font-medium text-ink sm:text-2xl" data-id="element-1433" data-mp="3bk5jn7nhoavw">
                What&apos;s your inventory worth?
              </h2>
              <p className="mt-1 text-xs text-ink/60 sm:text-sm" data-id="element-1434" data-mp="31r6v1przskca">
                Drag for a quick estimate, or type an exact amount.
              </p>

              <div className="mt-4 space-y-4" data-id="element-1435" data-mp="3u8sopq5o109h">
                <Field label="Excess MSRP value" value={<CurrencyInput value={excess} min={50000} max={250000000} onChange={setExcess} ariaLabel="Exact excess inventory value" data-id="element-1437" data-mp="1isipguz3pxt6" />} data-id="element-1436" data-mp="3u9kl3373720t">
                  <input type="range" min={50000} max={250000000} step={500000} value={excess} onChange={e => setExcess(Number(e.target.value))} className="w-full accent-forest" aria-label="Excess inventory value" data-id="element-1438" data-mp="1nlv3kb65mm7o" />
                </Field>

                <Field label={<span data-id="element-1440" data-mp="2ka1d9chxo4st">
                      Annual revenue{' '}
                      <span className="text-xs font-normal text-ink/45" data-id="element-1441" data-mp="2g97mmbd3wbw8">
                        — to estimate returns MSRP value
                      </span>
                    </span>} value={<CurrencyInput value={annualRevenue} min={500000} max={10000000000} onChange={setAnnualRevenue} ariaLabel="Exact annual revenue" data-id="element-1442" data-mp="1hwlksltoeii6" />} data-id="element-1439" data-mp="14w57lkqnnue6">
                  <input type="range" min={500000} max={10000000000} step={50000000} value={annualRevenue} onChange={e => setAnnualRevenue(Number(e.target.value))} className="w-full accent-forest" aria-label="Annual revenue" data-id="element-1443" data-mp="u93nkarn6s7t" />
                </Field>

                <Field label="Return rate" value={<span className="font-display text-lg font-semibold text-forest" data-id="element-1445" data-mp="fhlaolu2xdso">{returnRate}%</span>} data-id="element-1444" data-mp="bf87a42t1rz2">
                  <input type="range" min={0} max={60} step={1} value={returnRate} onChange={e => setReturnRate(Number(e.target.value))} className="w-full accent-forest" aria-label="Return rate" data-id="element-1446" data-mp="7yls2iefzqad" />
                </Field>

                <div className="rounded-xl border border-ink/10 bg-ink/[0.02] px-3 py-2.5" data-id="element-1447" data-mp="3l800c7jrwzi6">
                  <div className="flex items-baseline justify-between gap-3" data-id="element-1448" data-mp="3gdvtrpef6thm">
                    <label className="text-sm font-medium text-ink/80" data-id="element-1449" data-mp="3bgx1zdi8ayml">Time allowed to sell</label>
                    <span className="font-display text-base font-semibold text-forest" data-id="element-1450" data-mp="37ftimh0z2kgm">{sellWindowMonths} months</span>
                  </div>
                  <input type="range" min={2} max={12} step={1} value={sellWindowMonths} onChange={event => setSellWindowMonths(Number(event.target.value))} className="mt-1 w-full accent-forest" aria-label="Months allowed to sell inventory" data-id="element-1451" data-mp="32ha06qll7qt0" />
                  <div className="mt-0.5 flex justify-between text-[10px] text-ink/45" data-id="element-1452" data-mp="l5je0qh5sz3b">
                    <span data-id="element-1453" data-mp="6heujkaa8jjh">2 mo · 10¢ / $1 MSRP</span>
                    <span data-id="element-1454" data-mp="6flni3vrbxay">12 mo · 60¢ / $1 MSRP</span>
                  </div>
                </div>

                <div data-id="element-1455" data-mp="b9g0ypkrrg1l">
                  <div className="mb-1.5 flex items-baseline justify-between" data-id="element-1456" data-mp="b9ptajtadzag">
                    <label className="text-sm font-medium text-ink/80" data-id="element-1457" data-mp="3tnpajcxouaus">Recovery proceeds split</label>
                    <span className="text-xs text-ink/45" data-id="element-1458" data-mp="1ibllor6lt68">Must total 100%</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2" data-id="element-1459" data-mp="3joghcmand09h">
                    <TakeRateControl label="Brand take" value={brandTakeRate} onChange={setBrandTakeRate} data-id="element-1460" data-mp="3on1cw8eyevrl" />
                    <TakeRateControl label="ManyCo take" value={manyCoTakeRate} onChange={value => setBrandTakeRate(100 - value)} data-id="element-1461" data-mp="302ohvef9v71z" />
                  </div>
                </div>

                <div data-id="element-1462" data-mp="3nzu5ovx7idzb">
                  <label className="mb-1.5 block text-sm font-medium text-ink/80" data-id="element-1463" data-mp="3nzbyx5cx4hd5">
                    Category
                  </label>
                  <div className="flex flex-wrap gap-1.5" data-id="element-1464" data-mp="3j07s2ldguvpz">
                    {categories.map(c => <button key={c.label} onClick={() => setCategory(c)} className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${category.label === c.label ? 'border-forest bg-forest text-cream' : 'border-ink/15 text-ink/70 hover:border-ink/30'}`} data-id="element-1465" data-mp="1jhjhglvu8j9">
                        {c.label}
                      </button>)}
                  </div>
                </div>
              </div>
            </div>

            {/* Result */}
            <div className="flex flex-col justify-between bg-forest p-5 text-cream sm:p-6" data-id="element-1466" data-mp="3bmklpkbvtz7r">
              <div data-id="element-1467" data-mp="d2ud6rz33glk">
                <div className="flex items-center gap-1.5 text-cream/70" data-id="element-1468" data-mp="d3dm5j6raung">
                  <TrendingUpIcon className="h-3.5 w-3.5" data-id="element-1469" data-mp="85u8akdz0jw1" />
                  <span className="text-[11px] font-semibold uppercase tracking-wider" data-id="element-1470" data-mp="35pq8pxezh3p">
                    Estimated brand recovery
                  </span>
                </div>
                <p className="mt-2 min-h-9 overflow-hidden text-ellipsis whitespace-nowrap font-display text-2xl font-semibold leading-none tabular-nums sm:min-h-10 sm:text-3xl" data-id="element-1471" data-mp="3vbl6qxfn1otu">
                  {formatCurrency(result.brandProceeds)}
                </p>
                <p className="mt-1.5 overflow-hidden text-ellipsis whitespace-nowrap text-xs tabular-nums text-cream/70 sm:text-sm" data-id="element-1472" data-mp="zd3xqxzq1nag">
                  Likely range {formatCurrency(result.low)} – {formatCurrency(result.high)}
                </p>

                <div className="mt-4 space-y-1.5 border-t border-cream/15 pt-3 text-xs sm:text-sm" data-id="element-1473" data-mp="18awc9zxmcqws">
                  <Row label="Stranded inventory MSRP" value={formatCurrency(result.totalStranded)} data-id="element-1474" data-mp="1d5sd9pku2om1" />
                  <Row label="Returns MSRP / yr" value={formatCurrency(result.returnsValue)} data-id="element-1475" data-mp="1i551oercs3qq" />
                  <Row label="Time allowed to sell" value={`${sellWindowMonths} months`} data-id="element-1476" data-mp="1n3ztu7b7n4ni" />
                  <Row label="ManyCo recovery per MSRP $" value={formatCents(result.recoveryPerMsrpDollar)} data-id="element-1477" data-mp="1r4a2jrqe0962" />
                  <Row label="Brand take rate" value={`${brandTakeRate}%`} data-id="element-1478" data-mp="1w07gjre6h3fo" />
                </div>
                <p className="mt-3 text-[11px] leading-relaxed text-cream/60 sm:text-xs" data-id="element-1479" data-mp="3swy3tvjh0mj7">
                  Recovery rates vary by inventory quality, condition, and channel fit.
                </p>
                <div className="mt-3 rounded-xl border border-cream/20 bg-cream/[0.08] p-3" data-id="element-1480" data-mp="151kf81w0ftp7">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-cream/60" data-id="element-1481" data-mp="102g59919hrxt">
                    Traditional liquidation estimate
                  </p>
                  <p className="mt-0.5 font-display text-xl font-semibold tabular-nums text-cream" data-id="element-1482" data-mp="1909vc28fnpcj">
                    {formatCurrency(result.traditionalLiquidation)}
                  </p>
                  <p className="mt-0.5 text-[11px] leading-relaxed text-cream/65" data-id="element-1483" data-mp="1iy64wplq2c8j">
                    Based on an average 5¢ return per $1 of total MSRP through jobbers and off-price channels.
                  </p>
                </div>
              </div>

              <div className="mt-4" data-id="element-1484" data-mp="26otjftq58042">
                {submitted ? <div className="flex items-center gap-2 rounded-xl bg-cream/10 px-3 py-2.5 text-sm" data-id="element-1485" data-mp="26p3cjq6hindz">
                    <CheckIcon className="h-4 w-4" data-id="element-1486" data-mp="21nmavsg6ewwc" />
                    Thanks — a recovery specialist will reach out within one business day.
                  </div> : <form onSubmit={e => {
              e.preventDefault();
              setSubmitted(true);
            }} className="space-y-2" data-id="element-1487" data-mp="2q9tzsq93xo26">
                    <input type="email" required placeholder="Work email" className="w-full rounded-xl border border-cream/20 bg-cream/10 px-3 py-2.5 text-sm text-cream placeholder:text-cream/50 focus:border-cream/50 focus:outline-none" aria-label="Work email" data-id="element-1488" data-mp="22l12apcu785g" />
                    <button type="submit" className="group inline-flex w-full items-center justify-center gap-2 rounded-xl bg-cream px-4 py-2.5 text-sm font-semibold text-forest transition-colors hover:bg-white" data-id="element-1489" data-mp="1jm583e0zatrm">
                      Get my recovery report
                      <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-1" data-id="element-1490" data-mp="1bkxm7sb8avgf" />
                    </button>
                    <p className="text-center text-[11px] text-cream/50" data-id="element-1491" data-mp="16t63g3ybdu0u">
                      Free, no obligation. Estimates are directional.
                    </p>
                  </form>}
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
function Field({
  label,
  value,
  children
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  children: React.ReactNode;
}) {
  return <div data-id="element-1492" data-mp="ixl9e9uve20b">
      <div className="mb-1 flex items-baseline justify-between gap-3" data-id="element-1493" data-mp="ixv1q43e0l96">
        <label className="text-sm font-medium text-ink/80" data-id="element-1494" data-mp="2i9no76sju87n">{label}</label>
        {value}
      </div>
      {children}
    </div>;
}
function CurrencyInput({
  value,
  min,
  max,
  onChange,
  ariaLabel
}: {
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  ariaLabel: string;
}) {
  const [inputValue, setInputValue] = useState(value.toLocaleString('en-US'));
  useEffect(() => {
    setInputValue(value.toLocaleString('en-US'));
  }, [value]);
  const commitValue = () => {
    const parsed = Number(inputValue.replace(/[^0-9.]/g, ''));
    const nextValue = Number.isFinite(parsed) ? Math.min(Math.max(parsed, min), max) : value;
    onChange(nextValue);
    setInputValue(nextValue.toLocaleString('en-US'));
  };
  const handleChange = (nextInput: string) => {
    const digitsOnly = nextInput.replace(/[^0-9]/g, '');
    setInputValue(digitsOnly ? Number(digitsOnly).toLocaleString('en-US') : '');
  };
  return <span className="flex shrink-0 items-center rounded-md border border-forest/25 bg-forest/5 px-2 py-0.5 font-display text-sm font-semibold text-forest focus-within:border-forest focus-within:ring-2 focus-within:ring-forest/15" data-id="element-1495" data-mp="2gubkvli9lm3u">
      <span aria-hidden="true" data-id="element-1496" data-mp="3s9qh2n2ng9xw">$</span>
      <input type="text" inputMode="numeric" value={inputValue} onChange={event => handleChange(event.target.value)} onBlur={commitValue} onKeyDown={event => {
      if (event.key === 'Enter') {
        event.currentTarget.blur();
      }
    }} aria-label={ariaLabel} className="w-24 bg-transparent text-right outline-none sm:w-32" data-id="element-1497" data-mp="32iyt7npsqa0u" />
    </span>;
}
function TakeRateControl({
  label,
  value,
  onChange
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return <div className="rounded-lg border border-ink/10 bg-ink/[0.02] p-2.5" data-id="element-1498" data-mp="2tjlk4w5l3ngn">
      <div className="flex items-center justify-between gap-2" data-id="element-1499" data-mp="2osd8rukp1s0u">
        <label className="text-xs font-medium text-ink/65" data-id="element-1500" data-mp="2oqsg6ahxwj5m">{label}</label>
        <span className="font-display text-base font-semibold text-forest" data-id="element-1501" data-mp="211qzy12v83ps">{value}%</span>
      </div>
      <input type="range" min={0} max={100} step={1} value={value} onChange={event => onChange(Number(event.target.value))} className="mt-1 w-full accent-forest" aria-label={label} data-id="element-1502" data-mp="1w2mthi7bsk35" />
    </div>;
}
function Row({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="flex items-center justify-between" data-id="element-1503" data-mp="1lmoxmuq6rgg">
      <span className="text-cream/70" data-id="element-1504" data-mp="6nc3vdl2jp9t">{label}</span>
      <span className="font-medium" data-id="element-1505" data-mp="uc51rfl94772">{value}</span>
    </div>;
}